import { CheckIcon, DownloadIcon } from '@radix-ui/react-icons';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { Badge, Box, Flex, Grid, Heading, Text } from '@radix-ui/themes';
import {
  collectionHasShots,
  ensureInstallationDataCollections,
} from 'classes/helpers/install-data-collections.helper';
import { InstallHelper } from 'classes/helpers/install.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { getVisionPendingConfirmation } from 'classes/helpers/vision-install.helper';
import { CustomIcon } from 'components/common/custom-icon';
import { CommonDialog } from 'components/common/dialogs';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonNavMenu } from 'components/common/layout/nav-menu';
import { ScrollShadow } from 'components/common/scroll-shadow';
import {
  InstallationContext,
  PerformanceStepTabKey,
} from 'components/machine/dialogs/installation/context';
import { InstallationDetails } from 'components/machine/dialogs/installation/details';
import {
  StepBallTypeCalibration,
  StepCompletion,
  StepGeneralConfig,
  StepProjectorConfig,
  StepRepeatability,
  StepTestShotHoC,
  StepVisionCalibration,
} from 'components/machine/dialogs/installation/steps';
import {
  FiringLog,
  IFiringLog,
} from 'components/machine/dialogs/installation/steps/data-collector/firing-log';
import { StepHardwareChanges } from 'components/machine/dialogs/installation/steps/hardware-changes';
import {
  ActiveConnectionActionGroup,
  MachineConnectionTrigger,
} from 'components/main/sidebar/footer/connection-trigger';
import { MachineContext } from 'contexts/machine.context';
import { CustomIconPath } from 'enums/custom.enums';
import { SubStatus, VisionPendingKind } from 'enums/installation';
import { t } from 'i18next';
import { IButton } from 'interfaces/i-buttons';
import { IBaseDialog } from 'interfaces/i-dialogs';
import {
  IInstallStepDef,
  IVisionPendingDialogState,
} from 'interfaces/i-installation';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { getModelKey } from 'lib_ts/classes/ms.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { DataCollectionType } from 'lib_ts/enums/data-collection.enums';
import { InstallStep } from 'lib_ts/enums/installation';
import { MachineModelType } from 'lib_ts/enums/machine-models.enums';
import { InstallStepStatus } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IInstallationDetails } from 'lib_ts/interfaces/i-machine';
import { SpecialMsPosition } from 'lib_ts/interfaces/machine-msg/i-special-mstarget';
import { clamp } from 'lodash';
import {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AdminMachineModelsService } from 'services/admin/machine-models.service';
import { DataCollectionsService } from 'services/data-collections.service';
import { UserMachineModelsService } from 'services/machine-models.service';
import { PitchListsService } from 'services/pitch-lists.service';
import { VisionCameraCalibrationsService } from 'services/vision-camera-calibrations';
import slugify from 'slugify';
import { useAuthStore } from 'stores/use-auth-store';

// CS says they don't need a download button for model perf results
const ENABLE_MODEL_PERF_REPORTS = false;

const squircleCSS: CSSProperties = {
  borderRadius: '6px',
  width: '20px',
  height: '20px',
  justifyContent: 'center',
  alignItems: 'center',
};

export const InstallationDialogHoC = (props: IBaseDialog) => {
  const current = useAuthStore((s) => s.current);

  const { machine, createDataCollection, update } = useContext(MachineContext);

  const [steps, setSteps] = useState<IInstallStepDef[]>([]);

  // prevent double-spawning stuff in strict mode
  const [init, setInit] = useState(false);

  useEffect(() => {
    if (init) {
      return;
    }

    setInit(true);

    const fetchHasShots = async (id: string | null | undefined) =>
      id
        ? collectionHasShots(await DataCollectionsService.getShotSummaries(id))
        : false;

    if (machine.last_installation && !machine.last_installation.end_date) {
      // derive each calibration step's subStatus from its own collection's shots
      const resume = async () => {
        if (!machine.last_installation) {
          return;
        }

        const lastInstall = machine.last_installation;

        const btcHasShots = await fetchHasShots(lastInstall.btcID);
        const rtHasShots = await fetchHasShots(lastInstall.rtID);

        setSteps(
          InstallHelper.getSteps(lastInstall, machine, {
            btcHasShots,
            rtHasShots,
          })
        );
      };

      resume();
      return;
    }

    // prepare the installation
    const callback = async () => {
      const nextInstall: IInstallationDetails = {
        started_by: current.email,
        start_date: new Date().toISOString(),
        ball_type: machine.ball_type,
        ball_type_locked: false,

        btcID: null,
        end_date: null,
        finished_by: null,
        model_id: null,
        rtID: null,
        standalone: null,

        step_ball_type: null,
        step_completion: null,
        step_general_config: null,
        step_hardware_changes: null,
        step_projector_config: null,
        step_repeatability: null,
        step_test_shot: null,
        step_vision: null,

        vccID: null,
        vcdFt: null,
        vcdComplete: null,
      };

      const btcDc = await createDataCollection(
        DataCollectionType.BallTypeCalibration,
        machine.machineID,
        machine.ball_type
      );

      if (btcDc) {
        nextInstall.btcID = btcDc._id;
      }

      const rtDc = await createDataCollection(
        DataCollectionType.RepeatabilityTest,
        machine.machineID,
        machine.ball_type
      );

      if (rtDc) {
        nextInstall.rtID = rtDc._id;
      }

      if (machine.enable_trajekt_vision) {
        const vcc =
          await VisionCameraCalibrationsService.getInstance().createOne({
            machineID: machine.machineID,
            capturedFrames: [],
          });

        nextInstall.vccID = vcc?._id ?? null;
      }

      await update({
        _id: machine._id,
        last_installation: nextInstall,
      });

      // freshly created collections have no shots yet
      setSteps(
        InstallHelper.getSteps(nextInstall, machine, {
          btcHasShots: false,
          rtHasShots: false,
        })
      );
    };

    callback();
  }, [machine.machineID, machine.last_installation, machine.ball_type]);

  if (!machine.last_installation || !steps.length) {
    // wait until the effect is done updating the machine
    return null;
  }

  return <InstallationDialog {...props} steps={steps} setSteps={setSteps} />;
};

interface IProps extends IBaseDialog {
  steps: IInstallStepDef[];
  setSteps: (steps: IInstallStepDef[]) => void;
}

const InstallationDialog = (props: IProps) => {
  const { steps, setSteps } = props;

  const current = useAuthStore((s) => s.current);

  const {
    installation,
    updateInstallation,
    modelPerformance,
    machinePerformance,
    btc,
    btcTab,
    rt,
    rtTab,
    vcc,
    visionUi,
    reloadVcc,
  } = useContext(InstallationContext);

  const {
    machine,
    createDataCollection,
    update: updateMachine,
    setAutoFire,
    specialMsTarget,
  } = useContext(MachineContext);

  // TODO: Move these to InstallationContext after TMS migration complete
  const [rtFiringLog, setRTFiringLog] = useState<IFiringLog>({
    shotGroups: [],
    collection: rt,
  });

  const [btcFiringLog, setBTCFiringLog] = useState<IFiringLog>({
    shotGroups: [],
    collection: btc,
  });

  useEffect(() => {
    setBTCFiringLog((prev) => ({
      ...prev,
      collection: btc,
    }));
  }, [btc]);

  useEffect(() => {
    setRTFiringLog((prev) => ({
      ...prev,
      collection: rt,
    }));
  }, [rt]);

  // automatically mark the install as completed as soon as results step is completed
  useEffect(() => {
    if (installation.step_completion !== InstallStepStatus.Complete) {
      return;
    }

    if (!installation.end_date) {
      updateInstallation({
        end_date: new Date().toISOString(),
      });
      props.onClose();
    }
  }, [installation.step_completion, installation.end_date, props.onClose]);

  const isStandalone = !!installation.standalone;

  useEffect(() => {
    reloadVcc();
  }, [reloadVcc]);

  const [dialogConfirm, setDialogConfirm] = useState<number>();
  const [visionPendingDialog, setVisionPendingDialog] =
    useState<IVisionPendingDialogState>();

  const [activeIndex, setActiveIndex] = useState(() => {
    const firstIncomplete = steps.findIndex(
      (m) => !m.status || m.status === InstallStepStatus.Incomplete
    );

    return clamp(firstIncomplete, 0, steps.length - 1);
  });

  const activeStep = useMemo<IInstallStepDef>(
    () => steps[activeIndex],
    [steps, activeIndex]
  );

  const [maxStep, setMaxStep] = useState(activeIndex);

  useEffect(() => {
    // disable auto-fire whenever changing steps
    setAutoFire(false);

    // to allow users to return to any completed or skipped step but not future steps
    if (activeIndex > maxStep) {
      setMaxStep(activeIndex);
    }
  }, [activeIndex]);

  // mark the active step as either skipped or completed
  const updateStatus = async (
    status: InstallStepStatus,
    proceedToNextStep = false
  ) => {
    const step = steps[activeIndex];

    if (!step) {
      // couldn't find the step, shouldn't trigger
      console.warn(`failed to find install step w/ index ${activeIndex}`);
      return;
    }

    // upon completion for the first time, create data collection record for reference
    if (
      status === InstallStepStatus.Complete &&
      step.status !== InstallStepStatus.Complete
    ) {
      switch (step.id) {
        case InstallStep.BallType: {
          if (!btc) {
            console.warn('No BTC for installation');
            break;
          }

          await DataCollectionsService.updateOne(btc._id, {
            user: current.email,
            complete: true,
          });
          break;
        }

        case InstallStep.Repeatability: {
          if (!rt) {
            console.warn('No RT data collection ID for installation');
            break;
          }

          await DataCollectionsService.updateOne(rt._id, {
            user: current.email,
            complete: true,
          });
          break;
        }

        default: {
          break;
        }
      }
    }

    // save changes to the machine record
    const payload: Partial<IInstallationDetails> = {};

    switch (step.id) {
      case InstallStep.TestShot: {
        payload.step_test_shot = status;
        break;
      }

      case InstallStep.GeneralConfig: {
        payload.step_general_config = status;
        break;
      }

      case InstallStep.HardwareChanges: {
        payload.step_hardware_changes = status;
        break;
      }

      case InstallStep.ProjectorConfig: {
        payload.step_projector_config = status;
        break;
      }

      case InstallStep.VisionCalibration: {
        payload.step_vision = status;
        break;
      }

      case InstallStep.BallType: {
        payload.step_ball_type = status;

        if (installation.standalone === InstallStep.BallType) {
          // hides the reminder in the sidebar
          payload.end_date = new Date().toISOString();
        }

        updateInstallation(payload);
        break;
      }

      case InstallStep.Repeatability: {
        payload.step_repeatability = status;

        if (installation.standalone === InstallStep.Repeatability) {
          // hides the reminder in the sidebar
          payload.end_date = new Date().toISOString();
        }

        updateInstallation(payload);
        break;
      }

      case InstallStep.Completion: {
        payload.step_completion = status;
        break;
      }

      default: {
        console.warn(`encountered unexpected install step ${step.id}`);
        break;
      }
    }

    // don't wait so the UI moves ahead immediately
    updateInstallation(payload);

    // locally mark the step as completed
    step.status = status;
    setSteps([...steps]);

    // go to next step if possible
    if (proceedToNextStep && activeIndex + 1 < steps.length) {
      setActiveIndex(activeIndex + 1);
    }
  };

  const requestStepStatusChange = useCallback(
    (status: InstallStepStatus, proceedToNextStep = false) => {
      if (activeStep.id !== InstallStep.VisionCalibration) {
        updateStatus(status, proceedToNextStep);
        return;
      }

      const pending = getVisionPendingConfirmation({
        subStep: visionUi.subStep,
        vcc,
        distance: {
          vcdFt: installation.vcdFt,
          vcdComplete: installation.vcdComplete,
        },
        distancePendingComplete: visionUi.distancePendingComplete,
        isAdmin: current.role === UserRole.admin,
      });

      if (pending) {
        setVisionPendingDialog({
          key: Date.now(),
          kind: pending,
          status,
          proceedToNextStep,
        });
        return;
      }

      updateStatus(status, proceedToNextStep);
    },
    [
      activeStep.id,
      visionUi.subStep,
      visionUi.distancePendingComplete,
      installation.vcdFt,
      installation.vcdComplete,
      vcc,
      current.role,
      updateStatus,
    ]
  );

  const updateSubStatus = (status: SubStatus) => {
    const step = steps[activeIndex];

    if (!step) {
      // couldn't find the step, shouldn't trigger
      console.warn(`failed to find install step w/ index ${activeIndex}`);
      return;
    }

    // locally mark the step as completed
    step.subStatus = status;
    setSteps([...steps]);
  };

  const buttons = useMemo((): IButton[] => {
    const markAsIncompleteBtn: IButton = {
      label: 'Mark as Incomplete',
      variant: RADIX.BUTTON.VARIANT.SECONDARY,
      color: RADIX.COLOR.SUCCESS,
      onClick: () => updateStatus(InstallStepStatus.Incomplete),
      icon: <CustomIcon icon={CustomIconPath.SwitchOn} />,
    };

    const markAsCompleteBtn: IButton = {
      label: 'Mark as Complete',
      variant: RADIX.BUTTON.VARIANT.SECONDARY,
      onClick: () => requestStepStatusChange(InstallStepStatus.Complete),
      icon: <CustomIcon icon={CustomIconPath.SwitchOff} />,
    };

    const nextStepBtn: IButton = {
      label: 'Next Step',
      invisible: activeIndex + 1 >= steps.length,
      variant: RADIX.BUTTON.VARIANT.PRIMARY,
      onClick: () => updateStatus(InstallStepStatus.Complete, true),
    };

    const skipStepBtn: IButton = {
      label: 'Skip Step',
      invisible: activeIndex + 1 >= steps.length,
      variant: RADIX.BUTTON.VARIANT.SECONDARY,
      onClick: () => requestStepStatusChange(InstallStepStatus.Skipped, true),
    };

    const baseButtons: IButton[] =
      activeStep.status === InstallStepStatus.Complete
        ? [markAsIncompleteBtn, nextStepBtn]
        : [markAsCompleteBtn, skipStepBtn];

    switch (activeStep.id) {
      case InstallStep.TestShot: {
        return baseButtons;
      }

      case InstallStep.GeneralConfig: {
        return baseButtons;
      }

      case InstallStep.HardwareChanges: {
        return baseButtons;
      }

      case InstallStep.ProjectorConfig: {
        return baseButtons;
      }

      case InstallStep.VisionCalibration: {
        return baseButtons;
      }

      case InstallStep.BallType: {
        // hide Firing Log button if we don't have any shots to show yet
        const invisible =
          !btcFiringLog.shotGroups.length ||
          !btcFiringLog.shotGroups[0].shots.length;

        const output: IButton[] = [
          {
            label: 'Firing Log',
            color: RADIX.COLOR.SECONDARY,
            variant: 'soft',
            popoverProps: {
              side: 'top',
              className: 'no-padding max-width-unset',
              content: (
                <FiringLog id={installation.btcID || ''} log={btcFiringLog} />
              ),
            },
            invisible,
          },
        ];

        const onResultsTab = btcTab === PerformanceStepTabKey.Results;

        if (ENABLE_MODEL_PERF_REPORTS && modelPerformance && onResultsTab) {
          output.push({
            // model performance
            label: 'Download Reports',
            variant: RADIX.BUTTON.VARIANT.SECONDARY,
            icon: <DownloadIcon />,
            onClick: async () => {
              if (!modelPerformance) {
                return;
              }

              MiscHelper.saveAs(
                new Blob([
                  JSON.stringify(
                    {
                      details: installation,
                      performance: modelPerformance,
                    },
                    null,
                    2
                  ),
                ]),
                `${machine.machineID}-model-performance.json`
              );
            },
          });
        }

        if (isStandalone) {
          if (installation.model_id) {
            // complete button
            output.push({
              label: 'Complete Calibration',
              variant: RADIX.BUTTON.VARIANT.PRIMARY,
              onClick: () => {
                // todo: activate the new model?
                updateStatus(InstallStepStatus.Complete);
                updateInstallation({ end_date: new Date().toISOString() });
                props.onClose();
              },
            });
          }
        } else {
          if (installation.model_id) {
            // mark as complete/incomplete and next/skip
            output.push(...baseButtons);
          } else {
            // next/skip button
            output.push(baseButtons[1]);
          }
        }

        if (activeStep.subStatus === SubStatus.Error) {
          output.push({
            label: 'Restart',
            onClick: () => setDialogConfirm(Date.now()),
            variant: RADIX.BUTTON.VARIANT.PRIMARY,
          });
        }

        return output;
      }

      case InstallStep.Repeatability: {
        const invisible = !rtFiringLog.shotGroups.length;
        const output: IButton[] = [
          {
            label: 'Firing Log',
            color: RADIX.COLOR.SECONDARY,
            variant: 'soft',
            popoverProps: {
              side: 'top',
              className: 'no-padding max-width-unset',
              content: (
                <FiringLog id={installation.rtID || ''} log={rtFiringLog} />
              ),
            },
            invisible,
          },
        ];
        const onResultsTab = rtTab === PerformanceStepTabKey.Results;

        if (
          machinePerformance &&
          machinePerformance.images.length > 0 &&
          onResultsTab
        ) {
          output.push({
            // machine performance
            label: 'Download Reports',
            variant: RADIX.BUTTON.VARIANT.SECONDARY,
            icon: <DownloadIcon />,
            onClick: async () => {
              if (!machinePerformance) {
                return;
              }

              for (const img of machinePerformance.images) {
                const slug = slugify(img.filename, {
                  replacement: '_',
                  trim: true,
                });

                const url = `data:image/${img.extension};base64, ${img.base64}`;
                const res = await fetch(url);
                const blob = await res.blob();

                MiscHelper.saveAs(blob, `${machine.machineID}-${slug}`);
              }
            },
          });
        }

        if (isStandalone) {
          if (machinePerformance?.success) {
            // complete button
            output.push({
              label: 'Complete Test',
              variant: RADIX.BUTTON.VARIANT.PRIMARY,
              onClick: () => {
                updateStatus(InstallStepStatus.Complete);
                updateInstallation({ end_date: new Date().toISOString() });
                props.onClose();
              },
            });
          }
        } else {
          if (machinePerformance?.success) {
            // mark as complete/incomplete and next/skip
            output.push(...baseButtons);
          } else {
            // next/skip button
            output.push(baseButtons[1]);
          }
        }

        if (activeStep.subStatus === SubStatus.Error) {
          output.push({
            invisible: activeStep.subStatus !== SubStatus.Error,
            label: 'Restart',
            variant: RADIX.BUTTON.VARIANT.PRIMARY,
            onClick: () => setDialogConfirm(Date.now()),
          });
        }

        return output;
      }

      case InstallStep.Completion: {
        return [
          {
            label: 'Complete Machine Install',
            variant: RADIX.BUTTON.VARIANT.PRIMARY,
            onClick: () => {
              if (installation.end_date) {
                props.onClose();
              }

              if (!installation.end_date) {
                // allow useEffect to update end_date and close the dialog
                updateStatus(InstallStepStatus.Complete);
              }
            },
          },
        ];
      }

      default: {
        return [];
      }
    }
  }, [
    isStandalone,
    activeStep,
    machinePerformance,
    modelPerformance,
    updateStatus,
    requestStepStatusChange,
    installation.model_id,
    installation.btcID,
    installation.rtID,
    btcTab,
    rtTab,
    btcFiringLog,
    rtFiringLog,
  ]);

  const content = useMemo(() => {
    switch (activeStep.id) {
      case InstallStep.TestShot: {
        return <StepTestShotHoC />;
      }

      case InstallStep.GeneralConfig: {
        return <StepGeneralConfig />;
      }

      case InstallStep.HardwareChanges: {
        return <StepHardwareChanges />;
      }

      case InstallStep.ProjectorConfig: {
        return <StepProjectorConfig />;
      }

      case InstallStep.VisionCalibration: {
        return <StepVisionCalibration />;
      }

      case InstallStep.BallType: {
        return (
          <StepBallTypeCalibration
            state={activeStep.subStatus}
            onStart={() => {
              updateInstallation({ ball_type_locked: true });
              updateSubStatus(SubStatus.InProgress);
            }}
            onComplete={async () => {
              // edge case not handled:
              // user creates a model after having skipped pitches, but returns to Data Collection
              // to run the skipped pitches. Since we'll already have a model, they'll have to restart calibration
              if (installation.model_id) {
                // just move on
                updateSubStatus(SubStatus.Completed);
                return;
              }

              if (!btc) {
                // shouldn't trigger, but typescript needs this
                NotifyHelper.error({
                  message_md:
                    'Cannot proceed with ball type calibration without a valid collection ID. Please contact support.',
                });
                return;
              }

              NotifyHelper.warning({
                message_md:
                  'Creating model, please wait as this may take some time...',
              });

              // create a new model from the collected data
              const model =
                await AdminMachineModelsService.getInstance().trainCollectionModel(
                  {
                    model_type: MachineModelType.Carbon,
                    collectionID: btc.collectionID,
                    machineID: machine.machineID,
                    ball_type: installation.ball_type,
                  }
                );

              if (!model) {
                updateSubStatus(SubStatus.Error);
                return;
              }

              // mark the model as published since it's about to be used
              await UserMachineModelsService.getInstance().publishModel(
                model._id
              );

              // activate the model for the installation ball type
              await updateMachine({
                _id: machine._id,
                model_ids: {
                  ...machine.model_ids,
                  [getModelKey(machine)]: model._id,
                },
              });

              // record this model for the installation
              updateInstallation({
                model_id: model._id,
              });

              updateSubStatus(SubStatus.Completed);

              NotifyHelper.success({
                message_md: 'Model created successfully',
              });
            }}
            updateFiringLog={(shotGroups) =>
              setBTCFiringLog((prev) => ({
                ...prev,
                shotGroups,
              }))
            }
          />
        );
      }

      case InstallStep.Repeatability: {
        return (
          <StepRepeatability
            state={activeStep.subStatus}
            onStart={() => {
              updateInstallation({ ball_type_locked: true });
              updateSubStatus(SubStatus.InProgress);
            }}
            onComplete={() => {
              console.debug('onComplete triggered');
              updateSubStatus(SubStatus.Completed);
              specialMsTarget(SpecialMsPosition.lowered);
            }}
            updateFiringLog={(shotGroups) =>
              setRTFiringLog((prev) => ({
                ...prev,
                shotGroups,
              }))
            }
          />
        );
      }

      case InstallStep.Completion: {
        return <StepCompletion steps={steps} />;
      }

      default: {
        return (
          <Box className={RADIX.VFLEX.GROW}>
            {/* active step content */}
            You are on step {activeIndex + 1} (max is {maxStep + 1})
          </Box>
        );
      }
    }
  }, [
    activeIndex,
    maxStep,
    activeStep,
    steps,
    machine._id,
    machine.model_ids,
    installation.model_id,
    btc,
    rt,
  ]);

  const dialogHeader = useMemo(() => {
    switch (installation.standalone) {
      case InstallStep.Repeatability: {
        return 'common.repeatability-test';
      }

      case InstallStep.BallType: {
        return 'common.ball-type-calibration';
      }

      default: {
        return 'common.machine-install';
      }
    }
  }, [installation.standalone]);

  // for displaying the repeatability or ball type calibration list names once the list ID is set
  const [dialogSubHeader, setDialogSubHeader] = useState<string>();

  useEffect(() => {
    const callback = async () => {
      switch (installation.standalone) {
        case InstallStep.Repeatability: {
          if (!rt) {
            return;
          }

          const list = await PitchListsService.getInstance().getOne(rt.listID);

          setDialogSubHeader(
            list ? `${t('common.pitch-list')}: ${list.name}` : undefined
          );
          return;
        }

        case InstallStep.BallType: {
          if (!btc) {
            return;
          }

          const list = await PitchListsService.getInstance().getOne(btc.listID);

          setDialogSubHeader(
            list ? `${t('common.pitch-list')}: ${list.name}` : undefined
          );
          return;
        }

        default: {
          setDialogSubHeader(undefined);
          return;
        }
      }
    };

    callback();
  }, [installation.standalone, rt, btc]);

  return (
    <ErrorBoundary identifier="MachineInstallationDialog">
      {visionPendingDialog && (
        <CommonConfirmationDialog
          key={visionPendingDialog.key}
          identifier="VisionCalibrationPendingConfirm"
          title="Calibration not completed"
          onClose={() => setVisionPendingDialog(undefined)}
          content={
            <Box>
              {visionPendingDialog.kind === VisionPendingKind.Camera ? (
                <Text>
                  Stereo camera calibration has successful results ready. Click
                  &quot;Complete Calibration&quot; on the Results tab before
                  leaving this step, or continue anyway and leave the
                  calibration incomplete.
                </Text>
              ) : (
                <Text>
                  Distance calibration was successful. Click
                  &quot;Complete&quot; on the Data Collection tab before leaving
                  this step, or continue anyway and leave the calibration
                  incomplete.
                </Text>
              )}
            </Box>
          }
          cancel={{
            label: 'Continue Anyway',
            onClick: () => {
              const { status, proceedToNextStep } = visionPendingDialog;
              setVisionPendingDialog(undefined);
              updateStatus(status, proceedToNextStep);
            },
          }}
          action={{
            label: 'Go back and Finish',
            color: RADIX.COLOR.ACCENT,
            onClick: () => setVisionPendingDialog(undefined),
          }}
        />
      )}

      {dialogConfirm && (
        <CommonConfirmationDialog
          key={dialogConfirm}
          identifier="ConfirmDataCollectorReset"
          title="common.warning"
          content="This will reset your data collection progress and cannot be undone. Are you sure you want to proceed?"
          action={{
            label: 'common.reset',
            // variant: RADIX.BUTTON.VARIANT.PRIMARY,
            color: RADIX.COLOR.DANGER,
            onClick: async () => {
              switch (activeStep.id) {
                case InstallStep.BallType: {
                  const result = await ensureInstallationDataCollections({
                    machineID: machine.machineID,
                    ballType: installation.ball_type,
                    installation,
                    createDataCollection,
                    existingBtc: btc,
                    existingRt: rt,
                    forceRecreateBtc: true,
                  });

                  if (!result.btcID) {
                    NotifyHelper.error({
                      message_md:
                        'Failed to create a new data collection for ball type calibration.',
                    });
                    return;
                  }

                  updateInstallation({
                    btcID: result.btcID,
                    model_id: result.recreatedBtc
                      ? null
                      : installation.model_id,
                  });

                  updateSubStatus(SubStatus.NotStarted);
                  updateStatus(InstallStepStatus.Incomplete);
                  return;
                }

                case InstallStep.Repeatability: {
                  const result = await ensureInstallationDataCollections({
                    machineID: machine.machineID,
                    ballType: installation.ball_type,
                    installation,
                    createDataCollection,
                    existingBtc: btc,
                    existingRt: rt,
                    forceRecreateRt: true,
                  });

                  if (!result.rtID) {
                    NotifyHelper.error({
                      message_md:
                        'Failed to create a new data collection for repeatability testing.',
                    });
                    return;
                  }

                  updateInstallation({
                    rtID: result.rtID,
                  });

                  updateSubStatus(SubStatus.NotStarted);
                  updateStatus(InstallStepStatus.Incomplete);
                  return;
                }

                default: {
                  return;
                }
              }
            },
          }}
        />
      )}

      <CommonDialog
        {...props}
        modal
        width={RADIX.DIALOG.WIDTH.XL}
        vFlexHeight={RADIX.DIALOG.HEIGHT.LG}
        title={
          <Flex direction="column" gap="1">
            <Flex gap="2" align="center">
              <Heading size="5">{t(dialogHeader)}</Heading>
              <Badge size="1" variant="soft">
                {machine.machineID}
              </Badge>
            </Flex>

            {dialogSubHeader && (
              <Text color={RADIX.COLOR.HINT} size={RADIX.TEXT.SIZE.HINT}>
                {dialogSubHeader}
              </Text>
            )}
          </Flex>
        }
        leftFooter={
          <Flex gap={RADIX.FLEX.GAP.SM} align="center">
            <InstallationDetails />
            <MachineConnectionTrigger
              activeConnectionActionGroups={[
                ActiveConnectionActionGroup.Controls,
              ]}
              as="button"
            />
          </Flex>
        }
        buttons={buttons}
        content={
          <Grid
            columns="4"
            // bound the single row to the available height so the content
            // column scrolls internally instead of overflowing the dialog
            rows="1"
            className={RADIX.VFLEX.WRAPPER}
            gap={RADIX.FLEX.GAP.MD}
          >
            {!isStandalone && (
              <Box>
                {/* sidebar */}
                <CommonNavMenu orientation="vertical">
                  {steps.map((s, i) => {
                    const isActive = activeIndex === i;

                    const showCheckmark =
                      s.status === InstallStepStatus.Complete;

                    const labelColor = isActive
                      ? RADIX.COLOR.ACCENT
                      : i > maxStep
                        ? RADIX.COLOR.NEUTRAL
                        : undefined;

                    const squircleColor =
                      s.status === InstallStepStatus.Complete
                        ? RADIX.COLOR.SUCCESS
                        : RADIX.COLOR.NEUTRAL;
                    return (
                      <NavigationMenu.Item key={i}>
                        <NavigationMenu.Trigger
                          className={StringHelper.classNames([
                            'NavigationMenuTrigger',
                            isActive ? 'active' : 'inactive',
                          ])}
                          disabled={i > maxStep}
                          onClick={() => {
                            setActiveIndex(i);
                          }}
                        >
                          <Flex p="2" gap="2" align="center">
                            {showCheckmark ? (
                              <Badge
                                size="1"
                                color={squircleColor}
                                style={{
                                  ...squircleCSS,
                                  padding: 0,
                                }}
                              >
                                <CheckIcon />
                              </Badge>
                            ) : (
                              <Badge
                                size="1"
                                color={squircleColor}
                                style={squircleCSS}
                              >
                                {i + 1}
                              </Badge>
                            )}

                            <Text size="2" color={labelColor} truncate>
                              {t(s.label)}
                            </Text>
                          </Flex>
                        </NavigationMenu.Trigger>
                      </NavigationMenu.Item>
                    );
                  })}
                </CommonNavMenu>
              </Box>
            )}

            <ScrollShadow
              style={{ gridColumn: isStandalone ? 'span 4' : 'span 3' }}
              contentClassName={RADIX.VFLEX.COLUMN}
              contentStyle={{ paddingRight: 'var(--space-2)' }}
            >
              {content}
            </ScrollShadow>
          </Grid>
        }
      />
    </ErrorBoundary>
  );
};
